import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import ReCAPTCHA from "react-google-recaptcha";
import { API_CONFIG, SERVICES } from "../config";
import "./Register.css";

const TEST_RECAPTCHA_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

const Register = () => {
  const successRef = useRef(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    email: "",
  });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: "",
  });
  const [captchaValue, setCaptchaValue] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingGiftCode } = useAuth();
  const isLocalDevelopment = process.env.NODE_ENV === "development";
  const recaptchaSiteKey =
    SERVICES.RECAPTCHA.SITE_KEY ||
    (isLocalDevelopment ? TEST_RECAPTCHA_SITE_KEY : "");
  const isRecaptchaConfigured = Boolean(recaptchaSiteKey);

  // Password strength criteria
  const hasLowerCase = /[a-z]/.test(formData.password);
  const hasUpperCase = /[A-Z]/.test(formData.password);
  const hasNumber = /\d/.test(formData.password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
  const isLongEnough = formData.password.length >= 8;

  // Calculate password strength score (0-4)
  useEffect(() => {
    let score = 0;
    let feedback = "";

    if (formData.password.length === 0) {
      feedback = "";
    } else if (!isLongEnough) {
      feedback = "Password must be at least 8 characters long";
    } else {
      if (hasLowerCase) score++;
      if (hasUpperCase) score++;
      if (hasNumber) score++;
      if (hasSpecialChar) score++;

      switch (score) {
        case 1:
          feedback = "Weak password";
          break;
        case 2:
          feedback = "Fair password";
          break;
        case 3:
          feedback = "Good password";
          break;
        case 4:
          feedback = "Strong password";
          break;
        default:
          feedback = "";
      }
    }

    setPasswordStrength({ score, feedback });
  }, [
    formData.password,
    hasLowerCase,
    hasUpperCase,
    hasNumber,
    hasSpecialChar,
    isLongEnough,
  ]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const validateForm = () => {
    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // Check password strength
    if (passwordStrength.score < 3) {
      setError(
        "Password is not strong enough. Please include uppercase, lowercase, numbers, and special characters."
      );
      return false;
    }

    // Check CAPTCHA
    if (!isRecaptchaConfigured) {
      setError(
        "Registration is temporarily unavailable because CAPTCHA is not configured."
      );
      return false;
    }

    if (!captchaValue) {
      setError("Please complete the CAPTCHA verification");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!validateForm()) {
      return;
    }

    try {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...dataToSend } = formData;

      // Add captcha token to request
      dataToSend.recaptchaToken = captchaValue;

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/auth/register`,
        dataToSend
      );
      setMessage(response.data.message);

      // Scroll to success message
      setTimeout(() => {
        if (successRef.current) {
          successRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 5000);

      // Navigate to login using existing location state if provided; otherwise default to "/member"
      setTimeout(() => {
        navigate("/login", {
          state: { from: location.state?.from || "/member" },
        });
      }, 1000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Registration failed.");
    }
  };

  // Get password strength color
  const getPasswordStrengthColor = () => {
    switch (passwordStrength.score) {
      case 1:
        return "#ff4d4d"; // Red
      case 2:
        return "#ffaa00"; // Orange
      case 3:
        return "#2ecc71"; // Green
      case 4:
        return "#27ae60"; // Dark Green
      default:
        return "#ccc"; // Gray
    }
  };

  return (
    <div className="register-container">
      <h2>Register</h2>
      {error && <p className="error-message">{error}</p>}
      {message && (
        <div className="registration-success" ref={successRef}>
          <div className="success-icon">✓</div>
          <h3>Registration Successful!</h3>
          <p>{message}</p>
          <p className="email-emphasis">
            <strong>Important:</strong> Please check your email inbox for a
            verification link. You must verify your email address before you can
            log in.
          </p>
          <p className="redirect-notice">
            You will be redirected to the login page in a few seconds...
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="register-form">
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name:</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="username">Username:</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
          />
          <small className="form-text text-muted">
            Tip: You can use your email address as your username for easier
            login.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <small className="form-text text-muted">
            A verification email will be sent to this address. You must verify
            your email before logging in.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          {formData.password && (
            <div className="password-strength">
              <div className="strength-meter">
                <div
                  className="strength-meter-fill"
                  style={{
                    width: `${(passwordStrength.score / 4) * 100}%`,
                    backgroundColor: getPasswordStrengthColor(),
                  }}
                ></div>
              </div>
              <p
                className="strength-text"
                style={{ color: getPasswordStrengthColor() }}
              >
                {passwordStrength.feedback}
              </p>
            </div>
          )}
          <div className="password-requirements">
            <p>Password must:</p>
            <ul>
              <li className={isLongEnough ? "met" : ""}>
                Be at least 8 characters long
              </li>
              <li className={hasLowerCase ? "met" : ""}>
                Include lowercase letters
              </li>
              <li className={hasUpperCase ? "met" : ""}>
                Include uppercase letters
              </li>
              <li className={hasNumber ? "met" : ""}>Include numbers</li>
              <li className={hasSpecialChar ? "met" : ""}>
                Include special characters
              </li>
            </ul>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password:</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
          {formData.confirmPassword &&
            formData.password !== formData.confirmPassword && (
              <p className="error-message">Passwords do not match</p>
            )}
        </div>

        <div className="form-group captcha-container">
          {isRecaptchaConfigured ? (
            <ReCAPTCHA sitekey={recaptchaSiteKey} onChange={handleCaptchaChange} />
          ) : (
            <p className="error-message">
              Registration is temporarily unavailable. CAPTCHA is not configured.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={
            !isRecaptchaConfigured ||
            !formData.firstName ||
            !formData.lastName ||
            !formData.username ||
            !formData.email ||
            !formData.password ||
            !formData.confirmPassword ||
            formData.password !== formData.confirmPassword ||
            passwordStrength.score < 3 ||
            !captchaValue
          }
        >
          Register
        </button>
      </form>

      <p className="login-link">
        Already have an account? <Link to="/login">Login here</Link>.
      </p>
    </div>
  );
};

export default Register;
